/**
 * Varianten-Gruppierung der Werkbank-Leiste (T2/W3).
 *
 * **Rein clientseitig, auf dem bereits geladenen Puffer.** Keine Abfrage,
 * kein Hook, kein Supabase-Zugriff — die Funktion sieht nur, was die Leiste
 * ohnehin schon hat, und darum ist "mindestens zwei Treffer" wortwoertlich
 * gemeint: ein zweites Mitglied kann auf der naechsten Seite liegen und wird
 * dann in einem spaeteren Aufruf sichtbar, ohne dass diese Funktion je etwas
 * nachlaedt.
 *
 * **Reine Funktion.** Kein React-Import, kein Zustand — damit ist sie ohne
 * Renderbaum testbar, und `WorkbenchRail` entscheidet allein ueber
 * Sichtbarkeit (offene/geschlossene Gruppen); diese Funktion entscheidet nur
 * ueber Zugehoerigkeit und Reihenfolge.
 */

/** Minimale Produktform, die die Gruppierung braucht. Bewusst nicht
 * `WorkbenchProduct` importiert: die Funktion soll nicht an Felder gekoppelt
 * sein, die sie nie liest, und ein Test soll das Datenmodell nicht
 * nachbauen muessen. `WorkbenchRail` ruft die generische Funktion mit dem
 * echten Produkttyp auf, der diese Form erfuellt. */
export interface RailGroupableProduct {
  id: string;
  item_group_id: string | null | undefined;
  optimized_title: string | null | undefined;
}

export interface RailGroupRow<T> {
  kind: 'group';
  /** `item_group_id`, ungetrimmt aus dem ersten Mitglied — stabil als React-Key. */
  key: string;
  /** Laengster gemeinsamer Wortanfang der Mitgliedertitel, siehe `commonTitlePrefix`. */
  titel: string;
  count: number;
  produkte: T[];
}

export interface RailProductRow<T> {
  kind: 'product';
  product: T;
  /** `null` = kein Mitglied einer (qualifizierenden) Gruppe. */
  groupKey: string | null;
}

export type RailRow<T> = RailGroupRow<T> | RailProductRow<T>;

export interface RailGrouping<T> {
  rows: RailRow<T>[];
  /** Nur Produkte in einer QUALIFIZIERENDEN Gruppe (>= 2 Treffer) stehen hier. */
  groupOf: Map<string, string>;
}

/**
 * Laengster gemeinsamer Wortanfang aller Mitgliedertitel, an der letzten
 * Wortgrenze abgeschnitten und getrimmt.
 *
 * "An der letzten Wortgrenze abschneiden" heisst: der rohe Zeichenvergleich
 * kann mitten in einem Wort enden (z. B. bei "Sneaker Herren 42" gegen
 * "Sneaker Herren 44" nach der gemeinsamen "4"), und ein halbes Wort ist
 * keine brauchbare Ueberschrift. Der letzte Leerraum im rohen Praefix trennt
 * das letzte VOLLSTAENDIGE Wort vom angeschnittenen Rest dahinter.
 *
 * Der Schnitt greift NUR, wenn der rohe Praefix wirklich mitten in einem
 * Wort endet - erkennbar daran, dass er KUERZER ist als der kuerzeste
 * Mitgliedertitel (B6). Sind alle Titel identisch, ist der rohe Praefix der
 * volle (laengste) Titel: er endet am natuerlichen Titelende, nicht mitten
 * im Wort, und darf nicht um sein letztes Wort gekuerzt werden.
 *
 * Verglichen wird ueber Codepunkte (`Array.from`), nicht ueber rohe
 * UTF-16-Einheiten (B7): zwei Emoji aus derselben Ebene teilen sich oft den
 * hohen Surrogat-Codepunkt, ein Vergleich ueber `string[i]` koennte den
 * gemeinsamen Praefix mitten in einem Surrogatpaar enden lassen und eine
 * alleinstehende, kaputte Surrogat-Haelfte in die Ausgabe geben.
 *
 * Liegt das Ergebnis unter drei Zeichen (z. B. weil sich die Titel schon am
 * ersten Buchstaben trennen), traegt der Praefix keine brauchbare Aussage
 * mehr — dann gilt der Titel des ersten Mitglieds.
 */
function commonTitlePrefix<T extends RailGroupableProduct>(members: T[]): string {
  const titles = members.map((m) => m.optimized_title ?? '');
  const first = titles[0] ?? '';
  const titleChars = titles.map((title) => Array.from(title));

  let prefixChars = titleChars[0] ?? [];
  for (const chars of titleChars.slice(1)) {
    let i = 0;
    const max = Math.min(prefixChars.length, chars.length);
    while (i < max && prefixChars[i] === chars[i]) i += 1;
    prefixChars = prefixChars.slice(0, i);
    if (prefixChars.length === 0) break;
  }

  const minTitleLength = Math.min(...titleChars.map((chars) => chars.length));
  let atWordBoundary = prefixChars.join('');
  if (prefixChars.length < minTitleLength) {
    const lastSpaceIndex = prefixChars.lastIndexOf(' ');
    atWordBoundary =
      lastSpaceIndex >= 0 ? prefixChars.slice(0, lastSpaceIndex).join('') : atWordBoundary;
  }
  const trimmed = atWordBoundary.trim();

  return kappen(Array.from(trimmed).length < 3 ? first : trimmed);
}

/**
 * Deckelt die Gruppen-Ueberschrift auf eine lesbare Laenge.
 *
 * Live-Befund vom 08.09.2026 (Produktion, golfimport CH-DE, Gruppierung ueber
 * eine im Browser eingespielte `item_group_id`): tragen die Mitglieder keinen
 * brauchbaren gemeinsamen Praefix, faellt die Ueberschrift auf den Titel des
 * ersten Mitglieds zurueck, und der ist auf diesem Feed 170 Zeichen lang.
 * Sichtbar faellt das nicht auf, die Zeile ist `truncate` - aber der
 * zugaengliche Name der Gruppenzeile trug den ganzen Satz plus Variantenzahl
 * plus Zustand, und ein Screenreader liest ihn vollstaendig vor.
 *
 * 60 Codepunkte, nicht `TILE_TITLE_CUT` (70): das ist eine Ueberschrift in
 * einer 340 px breiten Leiste und keine Aussage darueber, was der Kunde bei
 * Google sieht. Geschnitten wird an der letzten Wortgrenze, damit kein
 * halbes Wort stehen bleibt, und in Codepunkten aus demselben Grund wie oben.
 */
const GRUPPENTITEL_MAX = 60;

function kappen(titel: string): string {
  const chars = Array.from(titel);
  if (chars.length <= GRUPPENTITEL_MAX) return titel;
  const geschnitten = chars.slice(0, GRUPPENTITEL_MAX);
  const letzterRaum = geschnitten.lastIndexOf(' ');
  const basis = letzterRaum >= 3 ? geschnitten.slice(0, letzterRaum) : geschnitten;
  return basis.join('').trimEnd();
}

/**
 * Gruppiert eine Produktliste nach `item_group_id` fuer die Leiste.
 *
 * Regeln (Plan T2/4):
 * - Gruppiert wird nach `item_group_id` NACH `trim()` — leer (auch reiner
 *   Leerraum) oder `null`/`undefined` gruppiert nie, selbst wenn mehrere
 *   Zeilen denselben (leeren) Wert tragen.
 * - Eine Gruppe braucht mindestens zwei Treffer IM UEBERGEBENEN PUFFER.
 * - Die Gruppenzeile steht an der Position des ERSTEN Mitglieds; alle
 *   weiteren Mitglieder werden aus ihrer Originalposition entfernt und
 *   folgen direkt als Block hinter der Gruppenzeile, in ihrer urspruenglichen
 *   Reihenfolge. Ungruppierte Zeilen behalten ihre Position relativ
 *   zueinander unveraendert — die Leiste bleibt damit die Reihenfolge der
 *   Abfrage (`last_optimized_at, id`), und ein spaeter nachgeladenes
 *   Mitglied faellt beim naechsten Aufruf in die bestehende Gruppe.
 */
export function groupRailRows<T extends RailGroupableProduct>(products: T[]): RailGrouping<T> {
  // Erster Durchgang: nur zaehlen, wer nach welchem (getrimmten) Schluessel
  // gruppieren WUERDE, ohne schon zu wissen, ob genug Treffer da sind.
  const buckets = new Map<string, T[]>();
  for (const product of products) {
    const key = product.item_group_id?.trim();
    if (!key) continue;
    const bucket = buckets.get(key);
    if (bucket) {
      bucket.push(product);
    } else {
      buckets.set(key, [product]);
    }
  }

  const groupOf = new Map<string, string>();
  const qualifying = new Map<string, T[]>();
  for (const [key, members] of buckets) {
    if (members.length < 2) continue;
    qualifying.set(key, members);
    for (const member of members) groupOf.set(member.id, key);
  }

  // Zweiter Durchgang: die Ausgabezeilen bauen, in der Reihenfolge der
  // Eingabe. Ein bereits emittierter Gruppenschluessel wird uebersprungen —
  // seine Mitglieder stehen schon als Block an der Position des ersten.
  const rows: RailRow<T>[] = [];
  const emitted = new Set<string>();

  for (const product of products) {
    const groupKey = groupOf.get(product.id);
    if (!groupKey) {
      rows.push({ kind: 'product', product, groupKey: null });
      continue;
    }
    if (emitted.has(groupKey)) continue;
    emitted.add(groupKey);

    const members = qualifying.get(groupKey)!;
    rows.push({
      kind: 'group',
      key: groupKey,
      titel: commonTitlePrefix(members),
      count: members.length,
      produkte: members,
    });
    for (const member of members) {
      rows.push({ kind: 'product', product: member, groupKey });
    }
  }

  return { rows, groupOf };
}
